// A phone's Back closes the sheet on top before it leaves the screen
// (APP_SHELL.md, Sheets and drawers; UX_STANDARDS UX-13; B-308;
// DIRECTION U20).
//
// WHY IT NEEDS AN ENTRY OF ITS OWN. Back walks the browser's history, and a
// sheet opening changed nothing there, so Back with a player's card open
// left the screen under it: My Team to whatever came before, the card gone
// with it (measured 2026-10-13 at 390). An app's Back takes the top thing
// away first.
//
// ONE ENTRY FOR ALL OF THEM, KEPT BY ONE PLACE. While any sheet is open, the
// history has one entry of ours on top, at the same address, marked. Each
// open sheet says so (`BackCloses`), and after each change the stack is put
// right in one go (`settle`), so a tap that closes one sheet and opens
// another (a card's Add opening the move sheet) moves nothing in history at
// all. (The first cut gave every sheet its own entry and let each push and
// pop it; the review of 2026-10-13 found a close and an open in one tap
// racing each other's Back, and a sheet that closes by being removed, such
// as the You menu, leaving its entry behind.) So:
//
//   BACK PAST OUR ENTRY CLOSES THE TOP SHEET. If others are still open
//   beneath it, the entry goes back on, ready for the next Back.
//
//   THE LAST SHEET CLOSING ANY OTHER WAY takes the entry back off, so ✕ and
//   then Back leaves the screen in one press.
//
//   A LINK OUT OF A SHEET leaves the entry behind, under the new page, where
//   nothing can take it out. Back onto it, with no sheet open, steps once
//   more at once, so Back from the team page a card linked to lands on the
//   screen the card was opened over. (In a browser tab, Forward onto it steps
//   back off; an installed app has no Forward.)
//
// The entry is pushed at the address the browser holds when the stack is put
// right, not the one last drawn, so a screen that took its query away as it
// opened a sheet (Trades' `?new=1`) does not get it back. The address never
// changes otherwise, so the screen under a sheet is not drawn or read again.

import { useEffect, useRef } from 'react';
import { useInRouterContext, useNavigate, type NavigateFunction } from 'react-router-dom';

/** The mark on our entry, in the router's own state for it. */
const MARK = 'fspSheet';

/** The sheets open now, oldest first: the last is the top. */
const open: { close: () => void }[] = [];
/** The router's `idx` of our entry while it is the one on screen, else null. */
let ours: number | null = null;
/**
 * Where to be once our entry has come off, when a sheet changed the address
 * in place on its way out: Compare's Add, an award's team (see `settle`).
 */
let carry: { pathname: string; search: string; hash: string; state: unknown } | null = null;
let navigate: NavigateFunction | null = null;
let pending = false;

function asObject(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

/** BrowserRouter keeps `{ usr, key, idx }` as each entry's state. */
function entryNow(): { idx: number | null; usr: Record<string, unknown> } {
  const state = asObject(window.history.state);
  return {
    idx: typeof state.idx === 'number' ? state.idx : null,
    usr: asObject(state.usr),
  };
}

/** Put the history right for the sheets open now: one entry of ours, or none. */
function settle(): void {
  pending = false;
  const go = navigate;
  if (go === null) return;
  const now = entryNow();
  const onOurs = ours !== null && now.idx === ours;
  if (open.length > 0 && !onOurs) {
    const { pathname, search, hash } = window.location;
    go({ pathname, search, hash }, { state: { ...now.usr, [MARK]: true } });
    // The router pushes at once; only its drawing waits.
    ours = entryNow().idx;
  } else if (open.length === 0 && onOurs) {
    ours = null;
    // A SHEET THAT CHANGED THE ADDRESS IN PLACE as it closed (a `replace`,
    // which keeps the entry's place and drops our mark) has said where the
    // screen is now. Taking our entry off would go back to the address before
    // that change and undo it — the pick Compare had just added, the trophy
    // room an award's team opened (the review of 2026-10-13). So the entry
    // under ours is put where this one is, as if the sheet had never had one.
    if (now.usr[MARK] !== true) {
      const { pathname, search, hash } = window.location;
      carry = { pathname, search, hash, state: window.history.state?.usr ?? null };
    }
    go(-1);
  } else if (open.length === 0) {
    ours = null;
  }
}

function soon(): void {
  if (pending) return;
  pending = true;
  // After every effect of this render: a sheet closing and another opening
  // in the same tap are one change.
  queueMicrotask(settle);
}

function onPop(): void {
  const now = entryNow();
  if (carry !== null && navigate !== null) {
    const { state, ...to } = carry;
    carry = null;
    navigate(to, { replace: true, state });
    return;
  }
  if (ours !== null && now.idx !== null && now.idx < ours) {
    // Back went past our entry: the top sheet closes, and if any are left
    // the entry goes back on for them.
    ours = null;
    open[open.length - 1]?.close();
    soon();
    return;
  }
  // Back (or Forward) onto an entry a sheet left behind, with none open.
  if (now.usr[MARK] === true && open.length === 0) window.history.back();
}

/**
 * Give a sheet its place in history while it is open. Renders nothing; a
 * sheet drawn outside a router (a unit test) simply has no Back to answer.
 */
export function BackCloses({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  return useInRouterContext() ? <Holds opened={opened} onClose={onClose} /> : null;
}

function Holds({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!opened) return;
    const sheet = { close: () => close.current() };
    open.push(sheet);
    soon();
    return () => {
      const at = open.indexOf(sheet);
      if (at !== -1) open.splice(at, 1);
      soon();
    };
  }, [opened]);
  return null;
}

/** The one keeper of the sheets' entry: mounted once, inside the router. */
export function SheetHistory() {
  const go = useNavigate();
  navigate = go;
  useEffect(() => {
    // A page opened fresh on an entry a sheet left (a reload): no sheet
    // survived it, so the mark goes and the entry stays, as the screen.
    const now = entryNow();
    if (now.usr[MARK] === true && open.length === 0) {
      const rest = { ...now.usr };
      delete rest[MARK];
      const { pathname, search, hash } = window.location;
      go(
        { pathname, search, hash },
        { replace: true, state: Object.keys(rest).length > 0 ? rest : null },
      );
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // Once, on purpose: `go` changes with every address, and `settle` reads
    // the newest through `navigate`.
  }, []);
  return null;
}
